import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { LinearProgress } from 'src/components/LinearProgress';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import {
  fetchOnboarding,
  fetchOnboardingKey,
} from 'src/models/platform-onboarding/platform-onboarding';
import { isHeadTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import { calculateOnboardingProgress } from 'src/utils/onboardingProgress';

export const OnboardingProgressPill = () => {
  const navigate = useNavigate();
  const { user, session, sessionTrainer } = useSession();
  const platformId = session?.id;
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const [isDismissed, setIsDismissed] = useState(false);

  const { data: onboarding } = useQuery({
    queryKey: fetchOnboardingKey(platformId),
    queryFn: () => (platformId ? fetchOnboarding(platformId) : null),
    enabled: !!platformId && user && isHeadTrainer(user),
  });

  // Check if onboarding feature flag is set, if not, don't render
  if (
    !isHeadTrainer(user) ||
    !session ||
    !sessionTrainer ||
    !onboarding ||
    !hasFeatureFlag(session, FeatureFlag.Onboarding) ||
    isDismissed
  ) {
    return null;
  }

  const { progressPercentage, completedSteps, totalSteps } =
    calculateOnboardingProgress(session, onboarding, sessionTrainer);

  // If onboarding is complete (100%), don't show the pill
  if (progressPercentage === 100) {
    return null;
  }

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsDismissed(true);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 48,
        zIndex: 1300,
        maxWidth: 200,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        border: (theme) => `1px solid ${theme.palette.success.main}`,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Dismiss button */}
      <IconButton
        onClick={handleDismiss}
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 20,
          height: 20,
          color: 'text.secondary',
          opacity: 0.6,
          '&:hover': {
            opacity: 1,
            color: 'text.primary',
            bgcolor: 'action.hover',
          },
        }}
        aria-label="Dismiss onboarding progress"
      >
        <Icon
          icon={faXmark}
          sx={{ fontSize: '0.75rem' }}
        />
      </IconButton>
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          pr: 3.5, // Add right padding to make room for dismiss button
          cursor: 'pointer',
        }}
        onClick={() => navigate('/onboarding')}
      >
        <Stack spacing={0.75}>
          <Typography
            variant="caption"
            color="text.primary"
            align="left"
            fontWeight="medium"
          >
            {tCommon('labels.onboarding')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 6,
              borderRadius: 2,
              bgcolor: (theme) =>
                `rgba(${theme.palette.success.mainChannel} / 0.1)`,
            }}
            color="success"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            fontSize="0.7rem"
            sx={{ opacity: 0.9 }}
          >
            {tCommon('labels.completedSteps', { completedSteps, totalSteps })}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
